/**
 * @license
 * Copyright Google Inc. All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */

import {scheduleMicroTask} from '../util';
/**
 * \@experimental Animation support is experimental.
 * @abstract
 */
export abstract class AnimationPlayer {
/**
 * @abstract
 * @param {?} fn
 * @return {?}
 */
onDone(fn: () => void) {}
/**
 * @abstract
 * @param {?} fn
 * @return {?}
 */
onStart(fn: () => void) {}
/**
 * @abstract
 * @param {?} fn
 * @return {?}
 */
onDestroy(fn: () => void) {}
/**
 * @abstract
 * @return {?}
 */
init() {}
/**
 * @abstract
 * @return {?}
 */
hasStarted() {}
/**
 * @abstract
 * @return {?}
 */
play() {}
/**
 * @abstract
 * @return {?}
 */
pause() {}
/**
 * @abstract
 * @return {?}
 */
restart() {}
/**
 * @abstract
 * @return {?}
 */
finish() {}
/**
 * @abstract
 * @return {?}
 */
destroy() {}
/**
 * @abstract
 * @return {?}
 */
reset() {}
/**
 * @abstract
 * @param {?} p
 * @return {?}
 */
setPosition(p: any) {}
/**
 * @abstract
 * @return {?}
 */
getPosition() {}
/**
 * @return {?}
 */
get parentPlayer(): AnimationPlayer|null { throw new Error('NOT IMPLEMENTED: Base Class'); }
/**
 * @param {?} player
 * @return {?}
 */
set parentPlayer(player: AnimationPlayer|null) { throw new Error('NOT IMPLEMENTED: Base Class'); }
}
/**
 * \@experimental Animation support is experimental.
 */
export class NoopAnimationPlayer implements AnimationPlayer {
private _onDoneFns: Function[] = [];
private _onStartFns: Function[] = [];
private _onDestroyFns: Function[] = [];
private _started = false;
private _destroyed = false;
private _finished = false;
public parentPlayer: AnimationPlayer|null = null;
constructor() {}
/**
 * @return {?}
 */
private _onFinish() {
    if (!this._finished) {
      this._finished = true;
      this._onDoneFns.forEach(fn => fn());
      this._onDoneFns = [];
    }
  }
/**
 * @param {?} fn
 * @return {?}
 */
onStart(fn: () => void): void { this._onStartFns.push(fn); }
/**
 * @param {?} fn
 * @return {?}
 */
onDone(fn: () => void): void { this._onDoneFns.push(fn); }
/**
 * @param {?} fn
 * @return {?}
 */
onDestroy(fn: () => void): void { this._onDestroyFns.push(fn); }
/**
 * @return {?}
 */
hasStarted(): boolean { return this._started; }
/**
 * @return {?}
 */
init(): void {}
/**
 * @return {?}
 */
play(): void {
    if (!this.hasStarted()) {
      scheduleMicroTask(() => this._onFinish());
      this._onStart();
    }
    this._started = true;
  }
/**
 * @return {?}
 */
private _onStart() {
    this._onStartFns.forEach(fn => fn());
    this._onStartFns = [];
  }
/**
 * @return {?}
 */
pause(): void {}
/**
 * @return {?}
 */
restart(): void {}
/**
 * @return {?}
 */
finish(): void { this._onFinish(); }
/**
 * @return {?}
 */
destroy(): void {
    if (!this._destroyed) {
      this._destroyed = true;
      if (!this.hasStarted()) {
        this._onStart();
      }
      this.finish();
      this._onDestroyFns.forEach(fn => fn());
      this._onDestroyFns = [];
    }
  }
/**
 * @return {?}
 */
reset(): void {}
/**
 * @param {?} p
 * @return {?}
 */
setPosition(p: number): void {}
/**
 * @return {?}
 */
getPosition(): number { return 0; }
}

function NoopAnimationPlayer_tsickle_Closure_declarations() {
/** @type {?} */
NoopAnimationPlayer.prototype._onDoneFns;
/** @type {?} */
NoopAnimationPlayer.prototype._onStartFns;
/** @type {?} */
NoopAnimationPlayer.prototype._onDestroyFns;
/** @type {?} */
NoopAnimationPlayer.prototype._started;
/** @type {?} */
NoopAnimationPlayer.prototype._destroyed;
/** @type {?} */
NoopAnimationPlayer.prototype._finished;
/** @type {?} */
NoopAnimationPlayer.prototype.parentPlayer;
}

